import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useLocation } from "../contexts/LocationContext";
import "./LocationDropdown.css";

// Set to false to disable all console logging in production
const DEBUG = true;
const log = (...args) => {
  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log("[LocationDropdown]", ...args);
  }
};

const LocationDropdown = () => {
  const {
    selectedCity,
    nearbyCities,
    allCities,
    isLoading,
    error,
    getUserLocation,
    selectCity,
    selectCustomLocation,
    selectGeocodedLocation,
    formatDistance,
  } = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [showNearby, setShowNearby] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  const searchInputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const dropdownRef = useRef(null);
  const pendingDetectCloseRef = useRef(false);

  // Simple in-memory cache to avoid re-filtering the same query
  const searchCacheRef = useRef({});

  // Always-fresh ref to allCities — the debounced setTimeout in handleSearchChange
  // can fire after React re-renders, so we need to read the latest list here
  // (the closure inside the timer would otherwise hold a stale snapshot).
  const allCitiesRef = useRef(allCities);
  useEffect(() => {
    allCitiesRef.current = allCities;
    log("allCities updated ->", {
      count: Array.isArray(allCities) ? allCities.length : 0,
      first3: Array.isArray(allCities)
        ? allCities.slice(0, 3).map((c) => c.name)
        : [],
    });
  }, [allCities]);

  // Auto-detect location on first visit only
  useEffect(() => {
    const hasAskedLocation = localStorage.getItem("location_permission_asked");
    if (!hasAskedLocation) {
      localStorage.setItem("location_permission_asked", "true");
      setTimeout(() => {
        getUserLocation();
      }, 1000);
    }
  }, []);

  const handleLocationClick = () => {
    if (isOpen) {
      setSearchQuery("");
      setShowNearby(false);
      setAutocompleteSuggestions([]);
      setShowSuggestions(false);
    }
    setIsOpen(!isOpen);
  };

  const handleCitySelect = (city) => {
    selectCity(city);
    setIsOpen(false);
    setSearchQuery("");
    setAutocompleteSuggestions([]);
    setShowSuggestions(false);
  };

  const handleDetectLocation = (e) => {
    e?.stopPropagation();
    pendingDetectCloseRef.current = true;
    setShowNearby(true);
    setShowSuggestions(false);
    getUserLocation(true);
  };

  // Local search over allCities. Replaces the previous Nominatim call which
  // had three production bugs:
  //   1. "Ko" matched "Kosgi" (international data) instead of Kochi/Kollam/Kottayam
  //   2. "Kollam" showed 3 times (multiple Nominatim records for the same city)
  //   3. 1-second debounce was needed to respect Nominatim's rate limit
  //
  // Reads from allCitiesRef (not allCities) so the debounced timer always
  // sees the freshest city list, even if React hasn't re-rendered yet.
  // Handles both the API shape (latitude/longitude) and the legacy shape
  // (lat/lng) so the search works regardless of which mapper produced the data.
  // Filters out inactive cities and cities without a name.
  const fetchPlaceSuggestions = useCallback((query) => {
    if (!query || !query.trim()) {
      setAutocompleteSuggestions([]);
      setShowSuggestions(false);
      setIsLoadingSuggestions(false);
      return;
    }

    const cities = allCitiesRef.current;
    log("fetchPlaceSuggestions", {
      query,
      citiesCount: Array.isArray(cities) ? cities.length : 0,
    });

    // If cities haven't loaded yet, show a loading state instead of empty results.
    // The next render after LocationContext populates allCities will re-trigger search.
    if (!Array.isArray(cities) || cities.length === 0) {
      setIsLoadingSuggestions(true);
      setAutocompleteSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const cacheKey = query.toLowerCase().trim();
    if (searchCacheRef.current[cacheKey]) {
      log("cache hit for", cacheKey);
      setAutocompleteSuggestions(searchCacheRef.current[cacheKey]);
      setShowSuggestions(true);
      setIsLoadingSuggestions(false);
      return;
    }

    const q = cacheKey;
    const ranked = cities
      .filter((c) => c && c.name)
      .filter((c) => c.available !== false && c.isActive !== false)
      .map((city) => {
        const name = String(city.name || "").toLowerCase();
        const state = String(city.state || "").toLowerCase();
        let rank = 99;
        if (name === q) {
          rank = 0; // exact match
        } else if (name.startsWith(q)) {
          rank = 1; // prefix match
        } else if (name.includes(q)) {
          rank = 2; // substring match in name
        } else if (state.includes(q)) {
          rank = 3; // substring match in state
        } else {
          return null; // no match
        }
        const lat = city.lat ?? city.latitude;
        const lng = city.lng ?? city.longitude;
        return {
          place_id: city.id,
          display_name: `${city.name}, ${city.state || ""}`
            .trim()
            .replace(/, $/, ""),
          short_name: city.name,
          town: city.name,
          city: city.name,
          state: city.state || "",
          district: "",
          lat: lat != null ? String(lat) : "",
          lon: lng != null ? String(lng) : "",
          _rank: rank,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a._rank - b._rank)
      .slice(0, 8);

    // Strip internal rank before caching / rendering
    const normalized = ranked.map(({ _rank, ...rest }) => rest);

    log("search result", {
      query,
      count: normalized.length,
      names: normalized.map((p) => p.short_name),
      topRanks: ranked
        .slice(0, 3)
        .map((p) => ({ name: p.short_name, rank: p._rank })),
    });

    searchCacheRef.current[cacheKey] = normalized;
    setAutocompleteSuggestions(normalized);
    setShowSuggestions(normalized.length > 0);
    setIsLoadingSuggestions(false);
  }, []);

  // Debounce 120ms — local search is instant, so we only debounce to avoid
  // filtering on every keystroke. Previously 500ms (for Nominatim rate limit).
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setShowNearby(false);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!query.trim()) {
      setAutocompleteSuggestions([]);
      setShowSuggestions(false);
      setIsLoadingSuggestions(false);
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchPlaceSuggestions(query);
    }, 120);
  };

  // CHANGE 6: Updated handlePlaceSelect to use short_name and state
  const handlePlaceSelect = (place) => {
    const query = searchQuery.toLowerCase().trim();
    const candidates = [
      place.town,
      place.short_name,
      place.display_name.split(",")[0].trim(),
    ].filter(Boolean);
    const matchedQueryName = candidates.find(
      (name) =>
        name.toLowerCase() === query ||
        name.toLowerCase().includes(query) ||
        query.includes(name.toLowerCase()),
    );

    selectGeocodedLocation({
      name:
        matchedQueryName ||
        place.short_name ||
        place.display_name.split(",")[0].trim(),
      state: place.state,
      lat: parseFloat(place.lat),
      lng: parseFloat(place.lon),
      formattedAddress: place.display_name,
      searchQuery: query,
    });

    setIsOpen(false);
    setSearchQuery("");
    setAutocompleteSuggestions([]);
    setShowSuggestions(false);
  };

  const handleCustomLocationSelect = () => {
    if (searchQuery.trim()) {
      selectCustomLocation(searchQuery.trim());
      setIsOpen(false);
      setSearchQuery("");
      setAutocompleteSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Close suggestions when clicking outside search box
  useEffect(() => {
    const handleClickOutside = (event) => {
      const searchContainer = searchInputRef.current;
      const suggestions = document.querySelector(".autocomplete-suggestions");
      if (
        searchContainer &&
        !searchContainer.contains(event.target) &&
        (!suggestions || !suggestions.contains(event.target))
      ) {
        setShowSuggestions(false);
      }
    };

    if (isOpen && showSuggestions) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, showSuggestions]);

  // Auto-close dropdown once detection completes (skip on permission denied).
  useEffect(() => {
    if (!pendingDetectCloseRef.current) return;
    if (isLoading) return;

    const denied =
      typeof error === "string" &&
      error.startsWith("Location permission denied");

    pendingDetectCloseRef.current = false;

    if (denied) {
      // User denied — keep dropdown open so they can retry or pick manually.
      return;
    }

    setIsOpen(false);
    setShowNearby(false);
    setSearchQuery("");
    setAutocompleteSuggestions([]);
    setShowSuggestions(false);
  }, [isLoading, error]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery("");
        setShowNearby(false);
        setAutocompleteSuggestions([]);
        setShowSuggestions(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // const filteredCities = useMemo(() => {
  //   if (!searchQuery.trim()) {
  //     return showNearby && nearbyCities.length > 0 ? nearbyCities : allCities;
  //   }
  //   const query = searchQuery.toLowerCase().trim();
  //   return allCities.filter(
  //     (city) =>
  //       city.name.toLowerCase().includes(query) ||
  //       city.state.toLowerCase().includes(query) ||
  //       city.id.toLowerCase().includes(query)
  //   );
  // }, [searchQuery, allCities, nearbyCities, showNearby]);

  // const hasCustomLocation =
  //   searchQuery.trim() &&
  //   !allCities.some(
  //     (city) =>
  //       city.name.toLowerCase() === searchQuery.toLowerCase().trim() ||
  //       city.id.toLowerCase() === searchQuery.toLowerCase().trim()
  //   ) &&
  //   autocompleteSuggestions.length === 0;

  // const displayCities = filteredCities;

  return (
    <div className="location-dropdown" ref={dropdownRef}>
      <button
        className="location-trigger"
        onClick={handleLocationClick}
        disabled={isLoading}
      >
        <img src="/assets/img/icons/ic-location.svg" alt="Location" />
        <span className="location-text">
          {isLoading ? "Detecting..." : selectedCity?.name || "Select City"}
        </span>
        <i className={`fas fa-chevron-down ${isOpen ? "open" : ""}`}></i>
      </button>

      {isOpen && (
        <div className="location-menu">
          <div className="location-menu-header">
            <h4>Select Your City</h4>
            <button
              className="detect-location-btn"
              onClick={handleDetectLocation}
              disabled={isLoading}
            >
              <i className="fas fa-location-crosshairs"></i>
              {isLoading ? "Detecting..." : "Detect My Location"}
            </button>
          </div>

          {/* Search Box */}
          <div className="location-search-container" ref={searchInputRef}>
            <div className="search-input-wrapper">
              <i className="fas fa-search location-search-icon"></i>
              <input
                type="text"
                className="location-search-input"
                placeholder="Search city..."
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => {
                  setShowNearby(false);
                  if (autocompleteSuggestions.length > 0) {
                    setShowSuggestions(true);
                  }
                }}
              />

              {searchQuery && !isLoadingSuggestions && (
                <button
                  className="location-search-clear"
                  onClick={() => {
                    setSearchQuery("");
                    setAutocompleteSuggestions([]);
                    setShowSuggestions(false);
                    if (debounceTimerRef.current) {
                      clearTimeout(debounceTimerRef.current);
                    }
                  }}
                  aria-label="Clear search"
                  type="button"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}              
            </div>

            {isLoadingSuggestions && (
              <div className="location-search-loading">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
            )}

            {/* CHANGE 7: Updated suggestion list UI — shows city name + state on two lines */}
            {showSuggestions && autocompleteSuggestions.length > 0 && (
              <div className="autocomplete-suggestions">
                {autocompleteSuggestions.map((place) => (
                  <button
                    key={place.place_id}
                    className="autocomplete-suggestion-item"
                    onClick={() => handlePlaceSelect(place)}
                    type="button"
                  >
                    <i className="fas fa-map-marker-alt"></i>
                    <div className="suggestion-text">
                      <span className="suggestion-city">
                        {place.short_name}
                      </span>
                      <span className="suggestion-state">
                        {place.district ? `${place.district}, ` : ""}
                        {place.state}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="location-error">
              <i className="fas fa-exclamation-triangle"></i>
              {error}
            </div>
          )}

          {isLoadingSuggestions && searchQuery && (
            <div className="location-loading-cities">
              <i className="fas fa-spinner fa-spin"></i>
              <span>Loading cities…</span>
            </div>
          )}
          {/* 
          {hasCustomLocation && !showSuggestions && !isLoadingSuggestions && (
            <div className="custom-location-section">
              <button
                className="custom-location-btn"
                onClick={handleCustomLocationSelect}
                type="button"
              >
                <i className="fas fa-map-marker-alt"></i>
                <div className="custom-location-info">
                  <span className="custom-location-name">Use "{searchQuery}"</span>
                  <span className="custom-location-hint">Set as custom location</span>
                </div>
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )} */}

          {/* {showNearby && nearbyCities.length > 0 && !searchQuery && (
            <div className="nearby-section">
              <h5>Nearby Cities</h5>
              <div className="city-list">
                {nearbyCities.map((city) => (
                  <button
                    key={city.id}
                    className={`city-item ${selectedCity.id === city.id ? 'selected' : ''}`}
                    onClick={() => handleCitySelect(city)}
                    type="button"
                  >
                    <div className="city-info">
                      <span className="city-name">{city.name}</span>
                      <span className="city-state">{city.state}</span>
                    </div>
                    <span className="city-distance">{formatDistance(city.distance)}</span>
                  </button>
                ))}
              </div>
            </div>
          )} */}

          {/* {displayCities.length > 0 && (
            <div className="all-cities-section">
              <h5>
                {showNearby && nearbyCities.length > 0 ? 'All Cities' : 'Available Cities'}
              </h5>
              <div className="city-list">
                {displayCities.map((city) => (
                  <button
                    key={city.id}
                    className={`city-item ${selectedCity.id === city.id ? 'selected' : ''}`}
                    onClick={() => handleCitySelect(city)}
                    type="button"
                  >
                    <div className="city-info">
                      <span className="city-name">{city.name}</span>
                      <span className="city-state">{city.state}</span>
                    </div>
                    {city.distance && (
                      <span className="city-distance">{formatDistance(city.distance)}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )} */}

          {searchQuery &&
            autocompleteSuggestions.length === 0 &&
            !isLoadingSuggestions && (
              <div className="no-results">
                <i className="fas fa-search"></i>
                <p>No cities found matching "{searchQuery}"</p>
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default LocationDropdown;
